/* ============================================================================
   Notification sounds, synthesised with the Web Audio API (no audio files).
     message → "ching ching": two bright bell strikes
     post    → one soft chime for a new listing in a topic you follow
   Preferences live on this device (localStorage), because sound is a device
   setting: you may want it on the phone and off on the office laptop.
   Browsers only allow audio after the person has tapped or typed once on the
   page, so the AudioContext is created on the first interaction.
   ========================================================================== */

const KEY = "ac_notify_v1";
const DEFAULTS = { msg: true, post: false, interests: [], vibrate: true };
let ctx = null;
let lastPlayed = { message: 0, post: 0 };

export function getPrefs() {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) || "null");
    return { ...DEFAULTS, ...(v && typeof v === "object" ? v : {}), interests: Array.isArray(v && v.interests) ? v.interests : [] };
  } catch (e) { return { ...DEFAULTS }; }
}
export function setPrefs(patch) {
  const next = { ...getPrefs(), ...patch };
  try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
  return next;
}

function audio() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}
export function unlockAudio() {
  const a = audio();
  if (a && a.state === "suspended") a.resume().catch(() => {});
}
if (typeof window !== "undefined") {
  const once = () => { unlockAudio(); ["pointerdown", "keydown", "touchend"].forEach((e) => window.removeEventListener(e, once, true)); };
  ["pointerdown", "keydown", "touchend"].forEach((e) => window.addEventListener(e, once, true));
}

/* One bell strike: a few inharmonic partials with fast attack and long decay. */
function bell(a, t, base, gain, length) {
  const out = a.createGain();
  out.gain.setValueAtTime(0.0001, t);
  out.gain.exponentialRampToValueAtTime(gain, t + 0.006);
  out.gain.exponentialRampToValueAtTime(0.0001, t + length);
  out.connect(a.destination);
  [[1, 1], [2.76, 0.45], [5.4, 0.22], [8.93, 0.08]].forEach(([ratio, amp]) => {
    const o = a.createOscillator();
    const g = a.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(base * ratio, t);
    g.gain.value = amp;
    o.connect(g); g.connect(out);
    o.start(t); o.stop(t + length + 0.05);
  });
}

export function play(kind, { force = false } = {}) {
  const prefs = getPrefs();
  if (!force && ((kind === "message" && !prefs.msg) || (kind === "post" && !prefs.post))) return false;
  const now = Date.now();
  if (!force && now - (lastPlayed[kind] || 0) < 1500) return false; // a burst of messages → one sound
  lastPlayed[kind] = now;
  const a = audio();
  if (!a) return false;
  if (a.state === "suspended") a.resume().catch(() => {});
  const t = a.currentTime + 0.02;
  if (kind === "message") {
    bell(a, t, 1318.5, 0.22, 0.55);          // E6
    bell(a, t + 0.13, 1760, 0.2, 0.9);        // A6  → "ching ching"
    if (prefs.vibrate && navigator.vibrate) navigator.vibrate([20, 60, 20]);
  } else {
    bell(a, t, 880, 0.12, 1.1);               // A5, softer single chime
  }
  return true;
}
